import { Logger } from '@utils/logger/logger';

type Attributes = Record<string, string | number | boolean>;
type ReportError = (attributes: Attributes, error: Error) => void;

interface RecoverableError {
	error: Error;
	componentStack?: string;
	digest?: string;
}

// Chrome frames read "at div (url)", Safari/Firefox frames read "div@url" or just "div".
export const firstComponentFrame = (componentStack?: string) => {
	const line = componentStack
		?.split('\n')
		.map((frame) => frame.trim())
		.find(Boolean);
	if (!line) return 'Unknown';
	return line
		.replace(/^at /, '')
		.replace(/ \(.*\)$/, '')
		.replace(/@.*$/, '');
};

const errorCode = (error: Error) =>
	error.message.match(/#(\d+)/)?.[1] ?? 'other';

const summarize = (values: string[]) => {
	const counts = new Map<string, number>();
	for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
	return Array.from(counts, ([value, count]) => `${value}x${count}`).join(' ');
};

const reportToNewRelic: ReportError = (attributes, error) => {
	Logger.error({
		log_message: 'Hydration mismatch (recoverable)',
		optionalParams: attributes,
		errorToLog: error,
		newRelic: true,
	});
};

// React reports each mismatch several times per failed hydration, so send one error per page load.
export const createHydrationErrorReporter = (
	report: ReportError = reportToNewRelic,
	delayMs = 1000,
) => {
	const collected: RecoverableError[] = [];
	let timer: ReturnType<typeof setTimeout> | undefined;

	const flush = () => {
		clearTimeout(timer);
		timer = undefined;
		const [first] = collected;
		if (!first) return;
		const frames = collected.map(({ componentStack }) =>
			firstComponentFrame(componentStack),
		);
		report(
			{
				error: first.error.message,
				componentName: frames[0],
				// New Relic drops longer string attributes; the innermost frames identify the component.
				componentStack: (first.componentStack ?? '').slice(0, 3500),
				digest: first.digest ?? '',
				hydrationErrorCount: collected.length,
				hydrationErrorCodes: summarize(
					collected.map(({ error }) => errorCode(error)),
				),
				hydrationErrorFrames: summarize(frames).slice(0, 1000),
			},
			first.error,
		);
		collected.length = 0;
	};

	const onRecoverableError = (
		error: unknown,
		errorInfo: { componentStack?: string; digest?: string } = {},
	) => {
		collected.push({
			error: error instanceof Error ? error : new Error(String(error)),
			componentStack: errorInfo.componentStack,
			digest: errorInfo.digest,
		});
		clearTimeout(timer);
		timer = setTimeout(flush, delayMs);
	};

	return { onRecoverableError, flush };
};
